

import React, { useEffect, useRef } from "react";
import { Routes, Route, useLocation } from "react-router";
import "./i18n";
import "./App.css";
import { useTranslation } from "react-i18next";
import { useCrypto } from "./context/CryptoContext";
import Dashboard from "./components/dashboard";
import Login from "./components/login";
import LandingPage from "./pages/landing/LandingPage";
import AboutPage from "./pages/about/AboutPage";
import SchemesPage from "./pages/schemes/SchemesPage";
import RightToInformationPage from "./pages/rightToInformation/RightToInformationPage";
import CareerPage from "./pages/career/CareerPage";
import ResourcesPage from "./pages/resources/ResourcesPage";
import AchievementsPage from "./pages/achievements/AchievementsPage";
import complianceMenuItems, {
  getComplianceSection,
  normalizeComplianceSection,
} from "./pages/resources/data/complianceMenuItems";
import GlobalChatbot from "./components/globalChatbot/GlobalChatbot";
import ContactUs from "./pages/contactUs/contactUs";
import FaqPage from "./pages/faqs/FaqPage";
import HelpPage from "./pages/help/HelpPage";
import UpdatesPage from "./pages/updates/UpdatesPage";
import ComingSoonPage from "./pages/comingSoon/ComingSoonPage";
import ExternalLinkConfirmation from "./components/commonComponent/ExternalLinkConfirmation/ExternalLinkConfirmation";
import CookieConsent from "./components/commonComponent/CookieConsent/CookieConsent";
import Sitemap from "./pages/landing/components/Header/Sitemap";
import useDocumentTitle from "./hooks/useDocumentTitle";

const normalizeLanguageCode = (languageCode = "") =>
  languageCode.toLowerCase().replace(/_/g, "-");

const getBaseLanguageCode = (languageCode = "") =>
  normalizeLanguageCode(languageCode).split("-")[0];

const syncDocumentLanguage = (languageCode = "en") => {
  const normalizedLanguage = normalizeLanguageCode(languageCode || "en");

  document.documentElement.lang = normalizedLanguage;
  document.documentElement.dir = "ltr";
};

function App() {
  const { i18n } = useTranslation();
  const { setAesKey } = useCrypto();
  const location = useLocation();
  useDocumentTitle(location.pathname);
  const inactivityTimerRef = useRef(null);
  const hideGlobalChatbot = location.pathname === "/register-login";

  useEffect(() => {
    if (location.pathname === "/register-login") {
      window.location.replace("https://milannmdfc.org/");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleLanguageChange = (languageCode) => {
      syncDocumentLanguage(languageCode || i18n.resolvedLanguage || "en");
    };

    handleLanguageChange(i18n.resolvedLanguage || i18n.language);
    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const handleBhashiniLanguageChange = (event) => {
      const languageCode = getBaseLanguageCode(event.detail?.languageCode);
      const nextLanguage = languageCode || "en";

      syncDocumentLanguage(nextLanguage);

      if (i18n.resolvedLanguage !== nextLanguage) {
        i18n.changeLanguage(nextLanguage);
      }

    };

    window.addEventListener(
      "bhashini-language-change",
      handleBhashiniLanguageChange,
    );

    return () => {
      window.removeEventListener(
        "bhashini-language-change",
        handleBhashiniLanguageChange,
      );
    };
  }, [i18n]);

  useEffect(() => {
    function resetTimer() {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(
        () => {
          alert("Session expired due to inactivity.");
          setAesKey(null);
          window.location.reload();
        },
        30 * 60 * 1000,
      );
    }

    async function generateKey() {
      try {
        if (window.crypto && window.crypto.subtle) {
          const key = await window.crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt", "decrypt"],
          );
          setAesKey(key);
        } else {
          console.warn("Crypto.subtle not available. Disabling encryption.");
          setAesKey(null);
        }
      } catch (err) {
        console.warn("Failed to generate encryption key:", err);
        setAesKey(null);
      }
      resetTimer();
    }

    const events = ["click", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    generateKey();

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [setAesKey]);

  return (
    <React.Fragment>
      <CookieConsent />
      {!hideGlobalChatbot && <div data-print-hide><GlobalChatbot /></div>}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about/:section?" element={<AboutPage />} />
        <Route path="/schemes/:section?" element={<SchemesPage />} />
        <Route
          path="/right-to-information/:section?"
          element={<RightToInformationPage />}
        />
        <Route path="/career/:section?" element={<CareerPage />} />
        <Route path="/updates" element={<UpdatesPage />} />
        <Route path="/frequently-asked-questions" element={<FaqPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route
          path="/resources/:section/:resourceDocument"
          element={<ResourcesPage />}
        />
        <Route path="/resources/:section?" element={<ResourcesPage />} />
        <Route path="/achievements/:section?" element={<AchievementsPage />} />
        <Route
          path="/compliance/:section/:resourceDocument"
          element={
            <ResourcesPage
              basePath="/compliance"
              pageLabel="Compliance"
              menuItems={complianceMenuItems}
              getSection={getComplianceSection}
              normalizeSection={normalizeComplianceSection}
            />
          }
        />
        <Route
          path="/compliance/:section?"
          element={
            <ResourcesPage
              basePath="/compliance"
              pageLabel="Compliance"
              menuItems={complianceMenuItems}
              getSection={getComplianceSection}
              normalizeSection={normalizeComplianceSection}
            />
          }
        />
        <Route path="/Sitemap" element={<Sitemap />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/login" element={<ComingSoonPage />} />
        <Route path="/register-login" element={null} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/contactUs" element={<ContactUs />} />
          <Route path="/contact" element={<ContactUs />} />
      <Route path="/contactUs" element={<ContactUs />} />

     
      </Routes>
      <ExternalLinkConfirmation />
    </React.Fragment>
  );
}

export default App;
